import { useState, useEffect, useCallback, useMemo } from "react";
import { isSacredAdminMode, getEditorAuth } from "@/lib/sacred-library/sacred-library-api";
import { useAuthContext } from "@/lib/auth/AuthContext";
import { isStaffOrAdminRole, normalizeRole } from "@/lib/auth/roles";

function workerEditorUser(authUser) {
  if (!authUser) return null;
  // authUser from AuthContext is mapped — use .role directly.
  const appRole = normalizeRole(authUser.role);
  if (!isStaffOrAdminRole(appRole)) return null;
  return {
    email: authUser.email,
    role: appRole,
    can_publish_directly: appRole === "admin",
    approval_required: appRole === "staff",
    source: "worker",
  };
}

export function useSacredAdmin() {
  const { user: authUser } = useAuthContext();
  const [adminMode, setAdminMode] = useState(isSacredAdminMode());
  const [editorUser, setEditorUser] = useState(getEditorAuth().user);

  const mergedEditor = useMemo(() => {
    const kvUser = getEditorAuth().user;
    return kvUser || workerEditorUser(authUser);
  }, [authUser, editorUser]);

  useEffect(() => {
    const sync = () => {
      setAdminMode(isSacredAdminMode());
      setEditorUser(getEditorAuth().user);
    };
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  useEffect(() => {
    if (mergedEditor?.source === "worker" && isStaffOrAdminRole(mergedEditor.role)) {
      try {
        if (localStorage.getItem("vedapath_admin") !== "true") {
          localStorage.setItem("vedapath_admin", "true");
          setAdminMode(true);
        }
      } catch {
        /* ignore */
      }
    }
  }, [mergedEditor]);

  const refresh = useCallback(() => {
    setAdminMode(isSacredAdminMode());
    setEditorUser(getEditorAuth().user);
  }, []);

  const role = mergedEditor?.role;
  const canEditLibrary = role === "admin" || role === "editor";
  const canEditPages = role === "admin" || role === "editor" || role === "staff";
  const canEdit = canEditLibrary || canEditPages;
  const isAdmin = role === "admin";
  const isStaffRole = role === "staff";
  const canPublishLibrary =
    isAdmin ||
    (role === "editor" && mergedEditor?.can_publish_directly && !mergedEditor?.approval_required);
  const canPublishPages =
    isAdmin || (role === "editor" && mergedEditor?.can_publish_directly && !mergedEditor?.approval_required);
  const isStaff = !!mergedEditor;

  return {
    adminMode,
    editorUser: mergedEditor,
    canEdit,
    canEditLibrary,
    canEditPages,
    isAdmin,
    isStaffRole,
    canPublish: canPublishLibrary,
    canPublishLibrary,
    canPublishPages,
    isStaff,
    refresh,
  };
}
